import { getMultiValueCookie, cookieExists } from './cookie-factory';
import {
  getVersion,
  PREFERENCES,
  GDPR_COOKIE_NAME,
  GDPR_COOKIE_MAX_AGE_IN_DAYS,
  LATEST_VERSION,
  PURPOSE_SCOPED_ID_VERSION,
} from './isomorphic';

// Late-bound references to avoid circular import with cmp-state
// These are set by cmp-state.ts during module initialization
let isPurposeScopedIdServiceEnabledFn: () => boolean = () => false;
let isCookieVersionRollbackEnabledFn: () => boolean = () => false;

/**
 * Registers the isPurposeScopedIdServiceEnabled function.
 * Called by cmp-state.ts to break the circular dependency.
 *
 * @param fn - The function that checks if purpose-scoped ID service is enabled
 */
export const registerIsPurposeScopedIdServiceEnabled = (
  fn: () => boolean,
): void => {
  isPurposeScopedIdServiceEnabledFn = fn;
};

/**
 * Registers the isCookieVersionRollbackEnabled function.
 * Called by cmp-state.ts to break the circular dependency.
 *
 * @param fn - The function that checks if cookie version rollback is enabled
 */
export const registerIsCookieVersionRollbackEnabled = (
  fn: () => boolean,
): void => {
  isCookieVersionRollbackEnabledFn = fn;
};

/**
 * Returns the effective "latest" version based on the feature flag.
 * Flag OFF → 3 (baseline), Flag ON → 4 (re-consent required)
 *
 * @returns The effective latest cookie version number
 */
export const getEffectiveLatestVersion = (): number =>
  isPurposeScopedIdServiceEnabledFn()
    ? PURPOSE_SCOPED_ID_VERSION
    : LATEST_VERSION;

/**
 * If enableCookieVersionRollback is true and the cookie version is ahead of
 * the effective latest version, downgrade it. This is intended for explicit
 * rollback scenarios where users who got v4 during an experiment need their
 * cookie version reset to v3 when the flag is turned off.
 * Preference values (information, adverts, tcString) are preserved.
 *
 * By default (enableCookieVersionRollback = false), this function is a no-op.
 * Cookie versions are never downgraded automatically to prevent accidental
 * downgrades when users navigate between sites with different feature flag states.
 */
export const maybeDowngradeCookieVersion = (): void => {
  if (!isCookieVersionRollbackEnabledFn()) return;
  if (!cookieExists(GDPR_COOKIE_NAME)) return;

  const gdprCookie = getMultiValueCookie(GDPR_COOKIE_NAME);
  const userVersion = getVersion(gdprCookie);
  const effectiveVersion = getEffectiveLatestVersion();

  if (userVersion > effectiveVersion) {
    gdprCookie.setValue(PREFERENCES.VERSION, effectiveVersion);
    gdprCookie.store(GDPR_COOKIE_MAX_AGE_IN_DAYS, { secure: false });
  }
};
